import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input, Modal } from 'semantic-ui-react';
import { findError, validationClasses } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import FormValidationError from '@/components/errors/FormValidationError';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import Actions from '@/components/buttons/Actions';
import OnOffLabel from '@/components/OnOffLabel';
import {
  hideEditDictionary,
  insertActiveDictionary,
  updateActiveDictionaryEdit,
  updateDictionaryProperties,
} from '../actions';
import { validateBasicDictionary } from '../helper';
import { resolveCatalogueTemplate } from '../../catalogue/helper';

const EditDictionaryDialog = ({
  category,
  code,
  description,
  dictionaryCode,
  dictionaries,
  dictionaryUUID,
  hide,
  insertDictionary,
  isNew,
  primary,
  isPersisted,
  name,
  originalName,
  saveErrors,
  saveStatus,
  sourceName,
  template,
  theme,
  updateActiveEditProperties,
  updateDictionary,
  unmanagedData,
}) => {
  const intl = useIntl();
  const headerText = () => (isNew
    ? intl.formatMessage({ id: 'save.dataset.dictionary.create.title' })
    : intl.formatMessage({ id: 'save.dataset.dictionary.update.title' }));

  const saveText = () => (isNew ? 'save.dataset.dictionary.create.button' : 'actions.modify');

  const setErrors = (newErrors) => (updateActiveEditProperties({ saveErrors: newErrors }));

  const formData = {
    code: dictionaryCode,
    description,
    name,
    primary,
    source_name: sourceName,
  };

  const checkForErrors = () => {
    const validationErrors = validateBasicDictionary(
      description,
      dictionaries,
      dictionaryCode,
      dictionaryUUID,
      name,
      originalName,
      sourceName,
    );
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      if (isNew) {
        insertDictionary(category, code, formData, template);
      } else {
        updateDictionary(category, code, dictionaryUUID, formData, template);
      }
    }
  };

  const codeError = findError(saveErrors, 'dictionary', [fields.CODE]);
  const nameError = findError(saveErrors, 'dictionary', [fields.NAME]);
  const sourceNameError = findError(saveErrors, 'dictionary', [fields.SOURCE_NAME]);
  const descriptionError = findError(saveErrors, 'dictionary', [fields.DESCRIPTION]);

  return (
    <DialogWrapper
      className="aridhia-edit-dictionary-dialog"
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={name}
      onClose={hide}
      onSave={submitForm}
      saveMessageId={saveText()}
      title={headerText()}
      theme={theme}
    >
      <Modal.Content>
        <Form autoComplete="off" onSubmit={submitForm}>
          <FormValidationError
            labelId="save.dataset.dictionary.code"
            error={codeError}
          >
            <Input
              disabled={isPersisted || saveStatus === states.PENDING}
              type="text"
              placeholder={intl.formatMessage({ id: 'save.dataset.dictionary.code.placeholder' })}
              value={dictionaryCode || ''}
              maxLength={globalFieldLengths.code}
              onChange={(e, { value }) => {
                updateActiveEditProperties({ dictionaryCode: value });
              }}
              className={validationClasses(codeError)}
            />
          </FormValidationError>
          <FormValidationError
            labelId="save.dataset.dictionary.name"
            error={nameError}
          >
            <Input
              autoFocus
              disabled={saveStatus === states.PENDING}
              type="text"
              placeholder={intl.formatMessage({ id: 'save.dataset.dictionary.name.placeholder' })}
              value={name}
              maxLength={globalFieldLengths.name}
              onChange={(e, { value }) => {
                updateActiveEditProperties({ dictionaryName: value });
              }}
              className={validationClasses(nameError)}
            />
          </FormValidationError>
          {unmanagedData && (
          <FormValidationError
            labelId="save.dataset.dictionary.source.name"
            error={sourceNameError}
          >
            <Input
              disabled={saveStatus === states.PENDING}
              type="text"
              placeholder={intl.formatMessage({ id: 'save.dataset.dictionary.source.name.placeholder' })}
              value={sourceName}
              maxLength={globalFieldLengths.sourceName}
              onChange={(e, { value }) => {
                updateActiveEditProperties({ dictionarySourceName: value });
              }}
              className={validationClasses(sourceNameError)}
            />
          </FormValidationError>
          )}
          <FormValidationError
            labelId="save.dataset.dictionary.description"
            error={descriptionError}
          >
            <MarkdownEditor
              maxEditorHeight={80}
              minPreviewHeight={94}
              disabled={saveStatus === states.PENDING}
              value={description || ''}
              onChange={(value) => {
                updateActiveEditProperties({ dictionaryDescription: value });
              }}
              className={validationClasses(descriptionError)}
            />
          </FormValidationError>
        </Form>
      </Modal.Content>
      <Actions
        minimumDataEntryMet={name}
        onClose={hide}
        onSave={submitForm}
        saveMessageId={saveText()}
      />
      <OnOffLabel
        className="flex edit-dictionary-primary-toggle"
        label={intl.formatMessage({ id: 'save.dataset.dictionary.primary' })}
        toggle
        checked={formData.primary}
        onClick={() => {
          updateActiveEditProperties({ primary: !formData.primary });
        }}
        disabled={saveStatus === states.PENDING}
      />
    </DialogWrapper>
  );
};

const mapStateToProps = ({
  datasets: {
    datasets,
    activeDictionaryEdit: {
      category, code, dictionaryDescription, dictionaryCode, dictionaryUUID, isNew, primary,
      isPersisted, dictionaryName, dictionarySourceName, originalName, saveErrors, saveStatus,
    },
  },
  catalogues: {
    templates,
  },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(
    datasets,
    templates,
    { category, code },
    { dictionaries: [] },
  );
  const unmanagedData = dataset.status?.managed_data.supported === false;

  return {
    category,
    code,
    dictionaries: dataset.dictionaries,
    description: dictionaryDescription,
    primary,
    dictionaryCode,
    dictionaryUUID,
    isNew,
    isPersisted,
    name: dictionaryName,
    sourceName: dictionarySourceName,
    originalName,
    saveErrors,
    saveStatus,
    template,
    unmanagedData,
  };
};

const mapDispatchToProps = {
  hide: hideEditDictionary,
  insertDictionary: insertActiveDictionary,
  updateActiveEditProperties: updateActiveDictionaryEdit,
  updateDictionary: updateDictionaryProperties,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditDictionaryDialog);
